import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { HojasApoyoService } from "./hojas-apoyo.service";
import {
  DatoFormato,
  DatosFormato,
  LISTAS_FORMATO,
  VERSION_FORMATO_VALIDA,
  datosFormato,
  fechaFormato,
  renglonFormato,
} from "../formato-fos005";
import {
  ACTIVOS_PUEDEN_SER,
  CLASIFICACION_ACTIVOS,
  DESCRIPCION_CATEGORIA_AMENAZA,
  INTRO_ESTRATEGIAS,
  NOTAS_EXCEL,
} from "../notas-excel";
import {
  ApoyoLista,
  CatAmenaza,
  CatClasificacionRiesgo,
  CatClasificacionSeveridad,
  CatEstrategia,
  CatNivelRiesgo,
  CatProbabilidad,
  CatSeveridad,
  CatValoracion,
  MatrizCatalogoReq,
  MatrizCatalogoRes,
  TipoCatalogo,
} from "../../shared/model/iso42001/matriz-riesgo.model";
import {
  CLASES_CATEGORIA,
  CLASES_ESTRATEGIA,
  clasePorTexto,
  renglonesCon,
} from "../expediente-comun/catalogos";

type Pestana = "estrategias" | "criterios" | "amenazas" | "apreciacion";

/** Un renglón en edición: de qué catálogo es y cuál es. */
interface EnEdicion {
  /** FORMATO: los datos del formato FOS-005, que no son un catálogo. */
  tipo: TipoCatalogo | "FORMATO";
  /** null mientras es un alta. */
  id: number | null;
}

/**
 * Configuración de las HOJAS DE APOYO del FOS-005 (Estrategias, Ejemplo de
 * amenazas y Apreciación de riesgos).
 *
 * Estas tres hojas son la referencia con la que se llena la Matriz de Riesgos
 * IA, y hasta ahora sólo se podían cambiar por SQL. Lo que se edita aquí son
 * LAS MISMAS TABLAS que la matriz lee para calcular y para pintar su ventana de
 * hojas de apoyo: no hay copia intermedia, así que el cambio se ve allá en
 * cuanto se vuelve a abrir esa ventana.
 *
 * Por lo mismo, la pantalla no decide sola qué se puede borrar: manda la baja y
 * el servidor contesta si ese renglón está en uso. Se pinta lo que el servidor
 * devuelve, nunca lo que la pantalla supone que quedó.
 */
@Component({
  selector: "app-hojas-apoyo-ia",
  standalone: true,
  templateUrl: "./hojas-apoyo-ia.component.html",
  styleUrls: ["./hojas-apoyo-ia.component.css"],
  imports: [CommonModule, FormsModule],
})
export class HojasApoyoIAComponent implements OnInit {
  pestana: Pestana = "estrategias";

  cargando = true;
  guardando = false;
  error = "";
  aviso = "";

  estrategias: CatEstrategia[] = [];
  amenazas: CatAmenaza[] = [];
  valoraciones: CatValoracion[] = [];
  probabilidades: CatProbabilidad[] = [];
  clasificaciones: CatClasificacionRiesgo[] = [];
  nivelesRiesgo: CatNivelRiesgo[] = [];
  severidades: CatSeveridad[] = [];
  clasSeveridad: CatClasificacionSeveridad[] = [];
  apoyoListas: ApoyoLista[] = [];

  /** Sólo un renglón en edición a la vez, en toda la pantalla. */
  edicion: EnEdicion | null = null;

  /**
   * De dónde se abrió la edición. Los niveles de severidad se ven en DOS
   * lugares de la misma página (la tabla grande y su tarjeta del trío); sin
   * esto, editar en una abriría el formulario también en la otra.
   */
  origenEdicion = "";
  /** La copia que se está capturando. Se descarta al cancelar. */
  borrador: any = null;

  /** Confirmación de baja, centrada. Se arma aquí y se pinta en el HTML. */
  confirma: { titulo: string; texto: string; detalle?: string; accion: () => void } | null = null;

  /* ---------------- Sub-pestañas y paginado ----------------
   *
   * Las amenazas apiladas por categoría eran un muro de 54 renglones, y la
   * valoración otro de 15: ahora cada categoría y cada dimensión es una
   * sub-pestaña, y una lista larga se pagina de a 10.
   *
   * El paginado vive en un diccionario por llave ('amz', 'val') y NUNCA se
   * guarda fuera de rango: pagina() acota contra el total en cada lectura,
   * así que si se elimina el último renglón de la última página, la vista
   * retrocede sola en lugar de quedarse mirando una página vacía.
   */
  categoriaActiva = "";
  dimensionActiva = "DISPONIBILIDAD";

  /** Las cuatro secciones de la hoja de Apreciacion, como paginas: apiladas,
   *  la pestaña medía más de cinco pantallas. */
  seccionApre = "valoracion";

  /* Dos paginas y no cuatro: las pildoras se encimaban en dos renglones.
   * Arriba lo que la hoja trae arriba (activos, amenazas, vulnerabilidades);
   * abajo la valoracion del riesgo con su criterio. Las claves 'amenazas' y
   * 'vulnerabilidades' ya no son pagina: sus bloques se muestran junto con
   * 'valoracion'. */
  readonly seccionesApre: { clave: string; titulo: string }[] = [
    { clave: "valoracion", titulo: "Valoración de Activos y amenazas" },
    { clave: "riesgo", titulo: "Valoración del riesgo y criterio de aceptación" },
  ];

  /** Las dos paginas de la hoja de Estrategias: en el Excel los criterios de
   *  evaluacion viven en ESA hoja, asi que aqui tambien. */
  seccionEst = "estrategias";
  readonly seccionesEst: { clave: string; titulo: string }[] = [
    { clave: "estrategias", titulo: "Riesgo y estrategias" },
    { clave: "criterios", titulo: "Criterios de evaluación" },
  ];
  readonly porPagina = 10;
  private paginas: { [llave: string]: number } = {};

  totalPaginas(total: number): number {
    return Math.max(1, Math.ceil(total / this.porPagina));
  }

  pagina(llave: string, total: number): number {
    return Math.min(this.paginas[llave] || 1, this.totalPaginas(total));
  }

  irPagina(llave: string, p: number, total: number): void {
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.paginas[llave] = Math.min(Math.max(1, p), this.totalPaginas(total));
  }

  rebanada(lista: any[], llave: string): any[] {
    const p = this.pagina(llave, lista.length);
    return lista.slice((p - 1) * this.porPagina, p * this.porPagina);
  }

  numerosPagina(total: number): number[] {
    const n = this.totalPaginas(total);
    const nums: number[] = [];
    for (let i = 1; i <= n; i++) {
      nums.push(i);
    }
    return nums;
  }

  /* ---------------- Categoria nueva con sus amenazas ----------------
   *
   * El alta de a una amenaza se queda para el dia a dia; esto es para
   * estrenar una categoria sin capturar renglon por renglon. El backend
   * guarda UNA amenaza por peticion, asi que aqui se despachan en fila.
   */
  altaCategoria = false;

  abrirAltaCategoria(): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.error = "";
    this.aviso = "";
    this.altaCategoria = true;
    // Ocupa el mismo candado que cualquier edicion: mientras el panel este
    // abierto no se toca nada mas.
    this.edicion = { tipo: "AMENAZA", id: null };
    this.borrador = { categoria: "", nombres: ["", "", ""] };
    this.enfocarEdicion();
  }

  agregarRenglonCategoria(): void {
    if (this.borrador && this.borrador.nombres) {
      this.borrador.nombres.push("");
    }
  }

  quitarRenglonCategoria(i: number): void {
    if (this.borrador && this.borrador.nombres && this.borrador.nombres.length > 1) {
      this.borrador.nombres.splice(i, 1);
    }
  }

  /** trackBy por posicion: sin el, cada tecla re-crea el input y roba el foco. */
  rastreaIndice(i: number): number {
    return i;
  }

  guardarCategoriaNueva(): void {
    if (!this.borrador || this.guardando) {
      return;
    }
    const cat = (this.borrador.categoria || "").trim();
    const nombres = (this.borrador.nombres || [])
      .map((n: string) => (n || "").trim())
      .filter((n: string) => n.length > 0);
    if (!cat) {
      this.error = "El nombre de la categoría es obligatorio.";
      return;
    }
    if (!nombres.length) {
      this.error = "Capture al menos una amenaza para la categoría.";
      return;
    }
    this.error = "";
    this.guardando = true;
    const base = this.siguienteOrden(this.amenazas);
    const cola = nombres.map((n: string, i: number) => ({
      categoria: cat,
      nombre: n,
      orden: base + i,
      estatus: "ACTIVO",
    }));
    this.guardarCola(cola, cat, cola.length);
  }

  /**
   * Una tras otra y NUNCA en paralelo: comparten el consecutivo de orden y en
   * paralelo llegarian revueltas. Si una falla se detiene ahi: las guardadas
   * ya estan en la base y el panel se queda solo con las que faltan, listas
   * para reintentar.
   */
  private guardarCola(cola: CatAmenaza[], cat: string, total: number): void {
    if (!cola.length) {
      this.guardando = false;
      this.altaCategoria = false;
      this.edicion = null;
      this.borrador = null;
      this.categoriaActiva = cat;
      this.aviso = total === 1
        ? 'Amenaza guardada en "' + cat + '".'
        : total + ' amenazas guardadas en "' + cat + '".';
      this.cdr.detectChanges();
      return;
    }
    const amenaza = cola[0];
    this.service.guardar({ tipo: "AMENAZA", amenaza }).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.detenerCola(cola, total, (res && res.mensajeError) || "");
          return;
        }
        this.recibir(res);
        this.guardarCola(cola.slice(1), cat, total);
      },
      error: () => this.detenerCola(cola, total, ""),
    });
  }

  private detenerCola(cola: CatAmenaza[], total: number, mensaje: string): void {
    this.guardando = false;
    const hechas = total - cola.length;
    this.error = (mensaje || 'No se pudo guardar "' + cola[0].nombre + '".')
      + (hechas > 0
        ? " Se guardaron " + hechas + " de " + total + "; las demás siguen en el panel."
        : "");
    if (this.borrador) {
      this.borrador.nombres = cola.map((c) => c.nombre);
    }
    this.cdr.detectChanges();
  }

  elegirCategoria(c: string): void {
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.categoriaActiva = c;
    this.paginas["amz"] = 1;
  }

  elegirSeccionApre(s: string): void {
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.seccionApre = s;
  }

  elegirSeccionEst(s: string): void {
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.seccionEst = s;
  }

  elegirDimension(d: string): void {
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.dimensionActiva = d;
    this.paginas["val"] = 1;
  }

  /** En el orden de la hoja del Excel: Disponibilidad, Integridad,
   *  Confidencialidad. La captura del activo lista C/I/D, pero la hoja de
   *  apoyo se coteja contra el formato impreso y ahi manda el formato. */
  readonly DIMENSIONES = ["DISPONIBILIDAD", "INTEGRIDAD", "CONFIDENCIALIDAD"];

  /** Notas del Excel, para las definiciones de los criterios. */
  readonly notas = NOTAS_EXCEL;

  /**
   * Introduccion de la hoja de Estrategias. Vive en iso42001_apoyo_lista
   * (INTRO_EST_TITULO / INTRO_EST_CUERPO) para poderse editar; el texto fijo
   * del formato queda de respaldo mientras esos renglones no existan, y en
   * ese caso no hay boton de editar porque no hay renglon que editar.
   */
  readonly introEstrategias = INTRO_ESTRATEGIAS;

  introFila(lista: string): ApoyoLista | null {
    const filas = this.apoyoListas.filter((a) => a.lista === lista);
    return filas.length ? filas[0] : null;
  }

  introTitulo(): string {
    const r = this.introFila("INTRO_EST_TITULO");
    return r && r.texto ? r.texto : this.introEstrategias.titulo;
  }

  introCuerpo(): string {
    const r = this.introFila("INTRO_EST_CUERPO");
    return r && r.texto ? r.texto : this.introEstrategias.cuerpo;
  }

  /** El texto guardado de una lista de un solo renglon; si no existe (la
   *  semilla no ha corrido), el texto fijo del formato. */
  apoyoTextoDe(lista: string, respaldo: string): string {
    const r = this.introFila(lista);
    return r && r.texto ? r.texto : respaldo;
  }

  /** Sin renglon en la base se considera activo: el respaldo siempre se ve. */
  apoyoActivo(lista: string): boolean {
    const r = this.introFila(lista);
    return !r || r.estatus === "ACTIVO";
  }

  editandoFila(r: ApoyoLista | null): boolean {
    return !!(r && r.idApoyoLista && this.editando("APOYO_LISTA", r.idApoyoLista));
  }

  /** Un color vivo por categoria del catalogo de amenazas (catalogos.ts). */
  claseCategoria(cat?: string): string {
    return clasePorTexto(cat, CLASES_CATEGORIA, "cat-otra");
  }

  /** El color que el Excel le da a cada estrategia (catalogos.ts). */
  claseEstrategia(nombre?: string): string {
    return clasePorTexto(nombre, CLASES_ESTRATEGIA, "est-neutra");
  }

  /* La cabecera de la hoja del Excel y sus definiciones, para pintar la
   * pestaña de Apreciación igual que el formato. */
  readonly activosPuedenSer = ACTIVOS_PUEDEN_SER;
  readonly clasifActivos = CLASIFICACION_ACTIVOS;

  /**
   * Renglones de una lista de la cabecera. Si la tabla todavía no existe o
   * viene vacía (producción antes de correr su SQL), se pinta el contenido
   * fijo del formato: sin id no salen botones, así que queda en solo lectura
   * y nada truena.
   */
  apoyoDe(lista: string): ApoyoLista[] {
    const filas = this.apoyoListas.filter((a) => a.lista === lista);
    if (filas.length) {
      return filas;
    }
    const fijo = lista === "ACTIVOS_PUEDEN_SER" ? this.activosPuedenSer
      : lista === "CLASIF_HARDWARE" ? this.clasifActivos.hardware
      : this.clasifActivos.software;
    return fijo.map((t) => ({ lista: lista, texto: t, estatus: "ACTIVO" }));
  }

  agregarApoyo(lista: string): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.error = "";
    this.aviso = "";
    this.edicion = { tipo: "APOYO_LISTA", id: null };
    this.borrador = {
      lista: lista,
      texto: "",
      orden: this.siguienteOrden(this.apoyoDe(lista)),
      estatus: "ACTIVO",
    };
    this.enfocarEdicion();
  }

  /**
   * El primer párrafo de una nota del Excel: es la DEFINICIÓN (qué es la
   * disponibilidad, qué es una amenaza...); el resto de la nota son los
   * niveles, que ya se pintan como tabla.
   */
  definicion(clave: string): string {
    return (NOTAS_EXCEL[clave] || "").split("\n\n")[0].replaceAll("\n", " ");
  }

  /** El renglon editable que corresponde a la definicion de cada dimension. */
  listaDimension(dim: string): string {
    const m: { [d: string]: string } = {
      CONFIDENCIALIDAD: "DEF_APRE_CONF",
      INTEGRIDAD: "DEF_APRE_INTEG",
      DISPONIBILIDAD: "DEF_APRE_DISP",
    };
    return m[dim] || "DEF_APRE_DISP";
  }

  definicionDimension(dim: string): string {
    const clave: { [d: string]: string } = {
      CONFIDENCIALIDAD: "confidencialidad",
      INTEGRIDAD: "integridad",
      DISPONIBILIDAD: "disponibilidad",
    };
    return this.definicion(clave[dim] || "");
  }
  readonly APLICA_A = ["AMENAZA", "VULNERABILIDAD"];
  /** Las dos escalas del riesgo de proceso; severidad = una por la otra. */
  readonly APLICA_SEV = ["PROBABILIDAD", "IMPACTO"];

  /** Máximos tomados de la definición de las tablas, no inventados. */
  readonly lim = {
    estrategiaNombre: 60,
    estrategiaDescripcion: 16000,
    amenazaCategoria: 60,
    amenazaNombre: 200,
    valoracionEtiqueta: 100,
    valoracionDescripcion: 16000,
    probabilidadEtiqueta: 50,
    probabilidadDescripcion: 255,
    clasificacion: 20,
    periodicidad: 20,
    criterio: 16000,
    severidadEtiqueta: 50,
    severidadDescripcion: 16000,
    apoyoTexto: 300,
    introCuerpo: 4000,
  };

  readonly secciones: { clave: Pestana; titulo: string; pie: string }[] = [
    { clave: "estrategias", titulo: "Estrategias", pie: "Riesgo, estrategias y criterios de evaluación" },
    { clave: "criterios", titulo: "Criterios de evaluación", pie: "Como en el Plan de Gestión" },
    { clave: "amenazas", titulo: "Ejemplo de amenazas", pie: "Catálogo por categoría" },
    { clave: "apreciacion", titulo: "Apreciación de riesgos", pie: "Activos, amenazas y vulnerabilidades" },
  ];

  /**
   * La pantalla arranca en SOLO LECTURA. Los botones de editar, eliminar y
   * agregar sólo aparecen al pulsar "Editar" arriba: así se puede consultar
   * la hoja sin miedo a mover algo con un clic distraído, que es justo lo
   * delicado aquí, porque estos catálogos son con los que la matriz calcula.
   */
  modoEdicion = false;

  constructor(
    private service: HojasApoyoService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando = true;
    this.service.consultar().subscribe({
      next: (res) => {
        this.recibir(res);
        this.cargando = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = "No se pudieron cargar los catálogos.";
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  private recibir(res: MatrizCatalogoRes): void {
    this.estrategias = res.estrategias || [];
    this.amenazas = res.amenazas || [];
    this.valoraciones = res.valoraciones || [];
    this.probabilidades = res.probabilidades || [];
    this.clasificaciones = res.clasificaciones || [];
    this.nivelesRiesgo = res.nivelesRiesgo || [];
    this.severidades = res.severidades || [];
    this.clasSeveridad = res.clasSeveridad || [];
    this.apoyoListas = res.apoyoListas || [];

    // La sub-pestaña activa tiene que apuntar a algo que exista: si su
    // categoría desapareció (se eliminó su última amenaza), se cae a la
    // primera disponible.
    const cats = this.categoriasAmenaza();
    if (cats.length && cats.indexOf(this.categoriaActiva) < 0) {
      this.categoriaActiva = cats[0];
    }
  }

  alternarModoEdicion(): void {
    if (this.edicion) {
      this.error = "Termine de guardar o cancele el renglón que está editando.";
      return;
    }
    this.modoEdicion = !this.modoEdicion;
    this.error = "";
    this.aviso = this.modoEdicion
      ? "Modo edición. Lo que cambie aquí lo toma la Matriz de Riesgos IA."
      : "";
  }

  cambiarPestana(p: Pestana): void {
    if (this.edicion) {
      this.error = "Termine de guardar o cancele el renglón que está editando.";
      return;
    }
    this.pestana = p;
    this.error = "";
    this.aviso = "";
  }

  /* ===================== Vistas agrupadas ===================== */

  /** Categorías del catálogo de amenazas, en el orden en que vienen. */
  categoriasAmenaza(): string[] {
    const vistas: string[] = [];
    this.amenazas.forEach((a) => {
      const c = a.categoria || "SIN CATEGORÍA";
      if (vistas.indexOf(c) < 0) {
        vistas.push(c);
      }
    });
    return vistas;
  }

  /** La descripcion del Excel para la categoria; vacia si es una nueva. */
  descripcionCategoria(cat: string): string {
    return DESCRIPCION_CATEGORIA_AMENAZA[(cat || "").trim().toUpperCase()] || "";
  }

  amenazasDe(categoria: string): CatAmenaza[] {
    return this.amenazas.filter((a) => (a.categoria || "SIN CATEGORÍA") === categoria);
  }

  valoracionPor(dimension: string): CatValoracion[] {
    return renglonesCon(this.valoraciones, "dimension", dimension);
  }

  probabilidadPor(aplicaA: string): CatProbabilidad[] {
    return renglonesCon(this.probabilidades, "aplicaA", aplicaA);
  }

  severidadPor(aplicaA: string): CatSeveridad[] {
    return renglonesCon(this.severidades, "aplicaA", aplicaA);
  }

  /** Del 4 al 1, que es como lista el Excel sus criterios. */
  severidadDesc(aplicaA: string): CatSeveridad[] {
    return this.severidadPor(aplicaA).slice().reverse();
  }

  /** Alta de probabilidad ya apuntada a su escala: el boton de la seccion de
   *  vulnerabilidades no debe nacer en AMENAZA. */
  agregarProbabilidadPara(aplicaA: string): void {
    this.agregar("PROBABILIDAD");
    if (this.borrador) {
      this.borrador.aplicaA = aplicaA;
    }
  }

  /** Alta de severidad ya apuntada a la escala de su seccion. */
  agregarSeveridadPara(aplicaA: string): void {
    this.agregar("SEVERIDAD");
    if (this.borrador) {
      this.borrador.aplicaA = aplicaA;
    }
  }

  /** Una celda de las 96, para pintar la tabla de valoración del riesgo. */
  celda(valorActivo: number, amenaza: number, vulnerabilidad: number): CatNivelRiesgo | undefined {
    return this.nivelesRiesgo.find(
      (n) =>
        n.valorActivo === valorActivo &&
        n.nivelAmenaza === amenaza &&
        n.nivelVulnerabilidad === vulnerabilidad
    );
  }

  claseNivel(nivel?: number): string {
    if (nivel === undefined || nivel === null) {
      return "";
    }
    for (const c of this.clasificaciones) {
      const desde = c.nivelDesde === undefined ? -1 : c.nivelDesde;
      const hasta = c.nivelHasta === undefined ? -1 : c.nivelHasta;
      if (nivel >= desde && nivel <= hasta) {
        return this.claseBanda(c.clasificacion);
      }
    }
    return "";
  }

  /**
   * Las bandas del riesgo del activo se llaman "Bajo/Medio/Alto/Muy Alto" y las
   * de severidad "Baja/Media/Alta/Critico". Se contemplan las dos formas: si
   * sólo se buscara la masculina, las de severidad saldrían todas en gris.
   */
  claseBanda(clasificacion?: string): string {
    const t = (clasificacion || "").toUpperCase();
    if (t.indexOf("MUY ALT") >= 0 || t.indexOf("CRITIC") >= 0 || t.indexOf("CRÍTIC") >= 0) {
      return "b-muyalto";
    }
    if (t.indexOf("ALT") >= 0) { return "b-alto"; }
    if (t.indexOf("MEDI") >= 0) { return "b-medio"; }
    if (t.indexOf("BAJ") >= 0) { return "b-bajo"; }
    return "";
  }

  /**
   * Los cuatro colores del formato para el renglón completo de una banda, los
   * mismos que usa la ventana de Criterios del Plan de Gestión.
   *
   * Se decide por coincidencia parcial y no por el texto exacto: desde esta
   * pantalla se le puede cambiar el nombre a una banda, y con un switch sobre
   * "Baja"/"Media"/"Alta"/"Critico" bastaba escribir "Crítico" con acento para
   * que la fila se quedara sin color.
   */
  claseFila(clasificacion?: string): string {
    const t = (clasificacion || "").toUpperCase();
    if (t.indexOf("MUY ALT") >= 0 || t.indexOf("CRITIC") >= 0 || t.indexOf("CRÍTIC") >= 0) {
      return "c-rojo";
    }
    if (t.indexOf("ALT") >= 0) { return "c-naranja"; }
    if (t.indexOf("MEDI") >= 0) { return "c-amarillo"; }
    if (t.indexOf("BAJ") >= 0) { return "c-verde"; }
    return "";
  }

  /** "1 a 4", tomado del propio catálogo. */
  rangoBanda(c: CatClasificacionSeveridad): string {
    return (c.nivelDesde === undefined || c.nivelDesde === null ? "" : c.nivelDesde)
      + " a " + (c.nivelHasta === undefined || c.nivelHasta === null ? "" : c.nivelHasta);
  }

  /* ===================== Edición ===================== */

  editando(tipo: TipoCatalogo, id?: number | null): boolean {
    if (!this.edicion) {
      return false;
    }
    const propio = id === undefined || id === null ? null : id;
    return this.edicion.tipo === tipo && this.edicion.id === propio;
  }

  /**
   * Si lo que se está capturando es de este catálogo, sea alta o cambio. Lo usa
   * la pestaña de Criterios, donde la captura no cabe dentro de la tarjeta y se
   * hace en un panel aparte, arriba.
   */
  editandoTipo(tipo: TipoCatalogo): boolean {
    return !!this.edicion && this.edicion.tipo === tipo;
  }

  /** Distingue el alta del cambio, para el título del panel. */
  esAlta(): boolean {
    return !!this.edicion && this.edicion.id === null;
  }

  /** Cualquier otro renglón se bloquea mientras uno está en edición. */
  bloqueado(tipo: TipoCatalogo, id?: number | null): boolean {
    return !!this.edicion && !this.editando(tipo, id);
  }

  avisoBloqueo(): void {
    this.error = "Ya hay un renglón en edición. Guarde o cancele antes de tocar otro.";
  }

  editar(tipo: TipoCatalogo, renglon: any): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.error = "";
    this.aviso = "";
    this.edicion = { tipo, id: this.idDe(tipo, renglon) };
    this.borrador = { ...renglon };
    this.enfocarEdicion();
  }

  /** Como editar(), pero recordando desde dónde: el formulario sólo se abre
   *  en ese lugar. Si el candado lo rechazó, el origen no se queda pegado. */
  editarDesde(origen: string, tipo: TipoCatalogo, renglon: any): void {
    this.origenEdicion = origen;
    this.editar(tipo, renglon);
    if (!this.edicion || !this.editando(tipo, this.idDe(tipo, renglon))) {
      this.origenEdicion = "";
    }
  }

  /**
   * Lleva la vista a lo que acaba de entrar en edicion. En tablas largas el
   * renglon marcado podia quedar fuera de pantalla y parecia que el boton no
   * habia hecho nada. El setTimeout espera a que Angular pinte la marca.
   */
  private enfocarEdicion(): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      // El mismo renglon puede estar marcado en dos lugares (tabla y trio):
      // se busca el que trae el FORMULARIO, no solo el resaltado.
      const marcados = Array.prototype.slice.call(document.querySelectorAll(".ha .en-edicion"));
      const conCampos = marcados.filter((m: Element) => m.querySelector("input, textarea, select"));
      const el = conCampos[0] || marcados[0] || document.querySelector(".ha .ha-alta");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }

  agregar(tipo: TipoCatalogo): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.error = "";
    this.aviso = "";
    this.edicion = { tipo, id: null };
    this.borrador = this.enBlanco(tipo);
    // El alta nace en la sub-pestaña que se está viendo; el campo sigue
    // editable por si se quiere estrenar categoría.
    if (tipo === "AMENAZA" && this.categoriaActiva) {
      this.borrador.categoria = this.categoriaActiva;
    }
    if (tipo === "VALORACION" && this.dimensionActiva) {
      this.borrador.dimension = this.dimensionActiva;
    }
    this.enfocarEdicion();
  }

  cancelar(): void {
    this.edicion = null;
    this.borrador = null;
    this.altaCategoria = false;
    this.origenEdicion = "";
    this.error = "";
  }

  private idDe(tipo: TipoCatalogo, r: any): number | null {
    if (tipo === "ESTRATEGIA") { return r.idCatEstrategia ?? null; }
    if (tipo === "AMENAZA") { return r.idCatAmenaza ?? null; }
    if (tipo === "VALORACION") { return r.idCatValoracion ?? null; }
    if (tipo === "PROBABILIDAD") { return r.idCatProbabilidad ?? null; }
    if (tipo === "CLASIFICACION") { return r.idCatClasificacion ?? null; }
    if (tipo === "SEVERIDAD") { return r.idCatSeveridad ?? null; }
    if (tipo === "CLAS_SEVERIDAD") { return r.idCatClasSev ?? null; }
    if (tipo === "APOYO_LISTA") { return r.idApoyoLista ?? null; }
    return r.idCatNivelRiesgo ?? null;
  }

  private enBlanco(tipo: TipoCatalogo): any {
    if (tipo === "ESTRATEGIA") {
      return { nombre: "", descripcion: "", orden: this.siguienteOrden(this.estrategias), estatus: "ACTIVO" };
    }
    if (tipo === "AMENAZA") {
      return { categoria: "", nombre: "", orden: this.siguienteOrden(this.amenazas), estatus: "ACTIVO" };
    }
    if (tipo === "VALORACION") {
      return { dimension: "CONFIDENCIALIDAD", nivel: 1, etiqueta: "", descripcion: "", estatus: "ACTIVO" };
    }
    if (tipo === "PROBABILIDAD") {
      return { aplicaA: "AMENAZA", nivel: 1, etiqueta: "", descripcion: "", estatus: "ACTIVO" };
    }
    if (tipo === "CLASIFICACION") {
      return { nivelDesde: 1, nivelHasta: 1, clasificacion: "", periodicidadMaxima: "", criterioAceptacion: "" };
    }
    if (tipo === "SEVERIDAD") {
      return { aplicaA: "PROBABILIDAD", nivel: 1, etiqueta: "", descripcion: "", estatus: "ACTIVO" };
    }
    return { nivelDesde: 1, nivelHasta: 1, clasificacion: "", criterioAceptacion: "" };
  }

  private siguienteOrden(lista: { orden?: number }[]): number {
    let mayor = 0;
    lista.forEach((x) => {
      if ((x.orden || 0) > mayor) {
        mayor = x.orden || 0;
      }
    });
    return mayor + 1;
  }

  /* ===================== Guardar ===================== */

  guardar(): void {
    if (!this.edicion || !this.borrador || this.guardando) {
      return;
    }
    // Los datos del formato se guardan con guardarFormato().
    if (this.edicion.tipo === "FORMATO") {
      return;
    }
    const falta = this.queFalta(this.edicion.tipo, this.borrador);
    if (falta) {
      this.error = falta;
      return;
    }

    const req: MatrizCatalogoReq = { tipo: this.edicion.tipo };
    if (this.edicion.tipo === "ESTRATEGIA") { req.estrategia = this.borrador; }
    if (this.edicion.tipo === "AMENAZA") { req.amenaza = this.borrador; }
    if (this.edicion.tipo === "VALORACION") { req.valoracion = this.borrador; }
    if (this.edicion.tipo === "PROBABILIDAD") { req.probabilidad = this.borrador; }
    if (this.edicion.tipo === "CLASIFICACION") { req.clasificacion = this.borrador; }
    if (this.edicion.tipo === "NIVEL_RIESGO") { req.nivelRiesgo = this.borrador; }
    if (this.edicion.tipo === "SEVERIDAD") { req.severidad = this.borrador; }
    if (this.edicion.tipo === "CLAS_SEVERIDAD") { req.clasSeveridad = this.borrador; }
    if (this.edicion.tipo === "APOYO_LISTA") { req.apoyoLista = this.borrador; }

    this.guardando = true;
    this.error = "";
    this.service.guardar(req).subscribe({
      next: (res) => {
        this.guardando = false;
        // El manejador de errores del backend contesta 200 con el cuerpo
        // vacío cuando algo de negocio falla: sin esta comprobación la
        // pantalla diría "guardado" sin que se hubiera guardado nada.
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError) || "No se pudo guardar el cambio.";
          this.cdr.detectChanges();
          return;
        }
        // Antes de soltar el borrador se apunta a dónde quedó lo guardado,
        // para que la vista se vaya ahí: guardar en una categoría nueva y
        // seguir viendo la anterior parecería que el alta se perdió.
        const tipoGuardado = this.edicion ? this.edicion.tipo : null;
        const catGuardada = tipoGuardado === "AMENAZA" && this.borrador
          ? (this.borrador.categoria || "").trim() : "";
        const dimGuardada = tipoGuardado === "VALORACION" && this.borrador
          ? this.borrador.dimension : "";
        this.recibir(res);
        if (catGuardada) {
          this.categoriaActiva = catGuardada;
        }
        if (dimGuardada) {
          this.dimensionActiva = dimGuardada;
        }
        this.edicion = null;
        this.borrador = null;
        this.origenEdicion = "";
        this.aviso = "Cambio guardado. La Matriz de Riesgos IA ya lo toma.";
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo guardar el cambio.";
        this.cdr.detectChanges();
      },
    });
  }

  /* ===================== Datos del formato FOS-005 =====================
   * Título, organización, versión y fecha de liberación (29/09/2026): son
   * los mismos para todas las matrices y salen en el encabezado de cada una
   * y en su Excel. Viven en iso42001_apoyo_lista (FMT_*); mientras no se
   * capturan valen los de siempre (formato-fos005.ts). */

  get formato(): DatosFormato {
    return datosFormato(this.apoyoListas);
  }

  fechaFormato(iso: string): string {
    return fechaFormato(iso);
  }

  /** Lo que se está escribiendo; null si no se editan. */
  formatoBorrador: DatosFormato | null = null;

  get editandoFormato(): boolean {
    return !!this.edicion && this.edicion.tipo === "FORMATO";
  }

  editarFormato(): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    this.error = "";
    this.aviso = "";
    // El mismo candado que cualquier edición: mientras esté abierto no se
    // toca nada más ni se cambia de pestaña.
    this.edicion = { tipo: "FORMATO", id: null };
    this.formatoBorrador = { ...this.formato };
  }

  cancelarFormato(): void {
    this.edicion = null;
    this.formatoBorrador = null;
    this.error = "";
  }

  guardarFormato(): void {
    const b = this.formatoBorrador;
    if (!b || this.guardando) {
      return;
    }
    const limpio: DatosFormato = {
      titulo: (b.titulo || "").trim(),
      organizacion: (b.organizacion || "").trim(),
      version: (b.version || "").trim(),
      liberacion: (b.liberacion || "").trim(),
    };
    if (!limpio.titulo) {
      this.error = "El título del formato es obligatorio.";
      return;
    }
    if (!limpio.organizacion) {
      this.error = "La organización es obligatoria.";
      return;
    }
    if (!VERSION_FORMATO_VALIDA.test(limpio.version)) {
      this.error = "La versión del formato va con números y puntos, por ejemplo 1.17.";
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(limpio.liberacion)) {
      this.error = "Falta la fecha de liberación del formato.";
      return;
    }
    // Solo lo que cambió. Cada dato es UN renglón: si ya existe se manda su
    // id; si no, el servidor lo crea (y si otro lo creó en medio, actualiza
    // ese en vez de duplicarlo).
    const actual = this.formato;
    const cola: ApoyoLista[] = (Object.keys(LISTAS_FORMATO) as DatoFormato[])
      .filter((d) => limpio[d] !== actual[d])
      .map((d) => {
        const r = renglonFormato(this.apoyoListas, d);
        return {
          idApoyoLista: r ? r.idApoyoLista : undefined,
          lista: LISTAS_FORMATO[d],
          texto: limpio[d],
          orden: r && r.orden != null ? r.orden : 0,
          estatus: "ACTIVO",
        };
      });
    if (!cola.length) {
      this.cancelarFormato();
      return;
    }
    this.error = "";
    this.guardando = true;
    this.guardarFormatoCola(cola);
  }

  /** Uno tras otro: si uno falla se detiene ahí y lo guardado se queda. */
  private guardarFormatoCola(cola: ApoyoLista[]): void {
    if (!cola.length) {
      this.guardando = false;
      this.edicion = null;
      this.formatoBorrador = null;
      this.aviso = "Datos del formato guardados. Todas las matrices ya los toman.";
      this.cdr.detectChanges();
      return;
    }
    this.service.guardar({ tipo: "APOYO_LISTA", apoyoLista: cola[0] }).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.guardando = false;
          this.error = (res && res.mensajeError) || "No se pudo guardar el dato del formato.";
          this.cdr.detectChanges();
          return;
        }
        this.recibir(res);
        this.guardarFormatoCola(cola.slice(1));
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo guardar el dato del formato.";
        this.cdr.detectChanges();
      },
    });
  }

  /** Validación en la pantalla. El servidor vuelve a validar lo mismo. */
  private queFalta(tipo: TipoCatalogo, b: any): string {
    const vacio = (t: any) => !t || String(t).trim().length === 0;

    if (tipo === "ESTRATEGIA") {
      if (vacio(b.nombre)) { return "El nombre de la estrategia es obligatorio."; }
      return "";
    }
    if (tipo === "AMENAZA") {
      if (vacio(b.categoria)) { return "La categoría es obligatoria."; }
      if (vacio(b.nombre)) { return "El nombre de la amenaza es obligatorio."; }
      return "";
    }
    if (tipo === "VALORACION") {
      if (vacio(b.dimension)) { return "La dimensión es obligatoria."; }
      if (b.nivel === null || b.nivel === undefined || b.nivel < 1 || b.nivel > 5) {
        return "El nivel de valoración va de 1 a 5.";
      }
      if (vacio(b.etiqueta)) { return "La etiqueta es obligatoria."; }
      return "";
    }
    if (tipo === "PROBABILIDAD") {
      if (vacio(b.aplicaA)) { return "Falta indicar a qué aplica."; }
      if (b.nivel === null || b.nivel === undefined || b.nivel < 1 || b.nivel > 4) {
        return "El nivel de probabilidad va de 1 a 4.";
      }
      if (vacio(b.etiqueta)) { return "La etiqueta es obligatoria."; }
      return "";
    }
    if (tipo === "CLASIFICACION") {
      if (b.nivelDesde === null || b.nivelDesde === undefined || b.nivelDesde < 1 || b.nivelDesde > 11) {
        return "El nivel inicial de la banda va de 1 a 11.";
      }
      if (b.nivelHasta === null || b.nivelHasta === undefined || b.nivelHasta < 1 || b.nivelHasta > 11) {
        return "El nivel final de la banda va de 1 a 11.";
      }
      if (b.nivelDesde > b.nivelHasta) { return "El nivel inicial no puede ser mayor que el final."; }
      if (vacio(b.clasificacion)) { return "La clasificación es obligatoria."; }
      if (vacio(b.periodicidadMaxima)) { return "La periodicidad máxima es obligatoria."; }
      return "";
    }
    if (tipo === "APOYO_LISTA") {
      if (vacio(b.texto)) { return "El texto es obligatorio."; }
      return "";
    }
    if (tipo === "SEVERIDAD") {
      if (vacio(b.aplicaA)) { return "Falta indicar a qué aplica."; }
      if (b.nivel === null || b.nivel === undefined || b.nivel < 1 || b.nivel > 4) {
        return "El nivel va de 1 a 4.";
      }
      if (vacio(b.etiqueta)) { return "La etiqueta es obligatoria."; }
      return "";
    }
    if (tipo === "CLAS_SEVERIDAD") {
      // La severidad es probabilidad x impacto: 1 a 4 por 1 a 4 = 1 a 16.
      if (b.nivelDesde === null || b.nivelDesde === undefined || b.nivelDesde < 1 || b.nivelDesde > 16) {
        return "El nivel inicial de la banda va de 1 a 16.";
      }
      if (b.nivelHasta === null || b.nivelHasta === undefined || b.nivelHasta < 1 || b.nivelHasta > 16) {
        return "El nivel final de la banda va de 1 a 16.";
      }
      if (b.nivelDesde > b.nivelHasta) { return "El nivel inicial no puede ser mayor que el final."; }
      if (vacio(b.clasificacion)) { return "La clasificación es obligatoria."; }
      return "";
    }
    if (b.nivelRiesgo === null || b.nivelRiesgo === undefined || b.nivelRiesgo < 0 || b.nivelRiesgo > 11) {
      return "El nivel de riesgo va de 0 a 11.";
    }
    return "";
  }

  /* ===================== Activar / desactivar ===================== */

  /**
   * Desactivar es la salida SEGURA cuando algo ya no se usa: deja de ofrecerse
   * en la matriz pero no se pierde, y lo ya capturado con ese valor se sigue
   * entendiendo. Eliminar, en cambio, borra el renglón, y por eso el servidor
   * lo impide si está en uso.
   *
   * No todos los catálogos tienen esta columna: las bandas de clasificación y
   * las 96 celdas no la traen, y ahí este botón no se pinta.
   */
  alternarEstatus(tipo: TipoCatalogo, renglon: any): void {
    if (!this.modoEdicion || this.edicion || this.guardando) {
      return;
    }
    const copia = { ...renglon };
    copia.estatus = copia.estatus === "ACTIVO" ? "INACTIVO" : "ACTIVO";

    const req: MatrizCatalogoReq = { tipo };
    if (tipo === "ESTRATEGIA") { req.estrategia = copia; }
    if (tipo === "AMENAZA") { req.amenaza = copia; }
    if (tipo === "VALORACION") { req.valoracion = copia; }
    if (tipo === "PROBABILIDAD") { req.probabilidad = copia; }
    if (tipo === "SEVERIDAD") { req.severidad = copia; }
    if (tipo === "APOYO_LISTA") { req.apoyoLista = copia; }

    this.guardando = true;
    this.error = "";
    this.aviso = "";
    this.service.guardar(req).subscribe({
      next: (res) => {
        this.guardando = false;
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError) || "No se pudo cambiar el estatus.";
          this.cdr.detectChanges();
          return;
        }
        this.recibir(res);
        this.aviso = copia.estatus === "ACTIVO"
          ? "Reactivado. La matriz vuelve a ofrecerlo."
          : "Desactivado. Deja de ofrecerse en la matriz, pero no se perdió.";
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo cambiar el estatus.";
        this.cdr.detectChanges();
      },
    });
  }

  activo(renglon: any): boolean {
    return renglon && renglon.estatus === "ACTIVO";
  }

  /* ===================== Baja ===================== */

  pedirBaja(tipo: TipoCatalogo, renglon: any, comoSeLlama: string): void {
    if (!this.modoEdicion) {
      return;
    }
    if (this.edicion) {
      this.avisoBloqueo();
      return;
    }
    const id = this.idDe(tipo, renglon);
    if (id === null) {
      return;
    }
    this.confirma = {
      titulo: "¿Eliminar este renglón?",
      texto: comoSeLlama,
      detalle:
        "Si algún activo o riesgo lo está usando, el sistema no lo dejará borrar y se lo dirá. " +
        "También puede dejarlo como Inactivo en lugar de eliminarlo: así deja de ofrecerse " +
        "sin perder lo ya capturado.",
      accion: () => this.eliminar(tipo, id),
    };
  }

  confirmarSi(): void {
    const accion = this.confirma ? this.confirma.accion : null;
    this.confirma = null;
    if (accion) {
      accion();
    }
  }

  confirmarNo(): void {
    this.confirma = null;
  }

  private eliminar(tipo: TipoCatalogo, id: number): void {
    this.guardando = true;
    this.error = "";
    this.aviso = "";
    this.service.eliminar({ tipo, id }).subscribe({
      next: (res) => {
        this.guardando = false;
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError) || "No se pudo eliminar el renglón.";
          this.cdr.detectChanges();
          return;
        }
        this.recibir(res);
        this.aviso = "Renglón eliminado.";
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo eliminar el renglón.";
        this.cdr.detectChanges();
      },
    });
  }

  /* ===================== Utilidades de plantilla ===================== */

  /**
   * Los ejes REALES de la tabla de valoración del riesgo, tomados de la
   * base: el valor del activo va de 0 a 5 (6 valores) y la amenaza y la
   * vulnerabilidad de 1 a 4. 6 x 4 x 4 = las 96 combinaciones.
   */
  readonly VALORES = [0, 1, 2, 3, 4, 5];
  readonly NIVELES_14 = [1, 2, 3, 4];

  rastreaPorId(_: number, r: any): any {
    return (
      r.idCatEstrategia ??
      r.idCatAmenaza ??
      r.idCatValoracion ??
      r.idCatProbabilidad ??
      r.idCatClasificacion ??
      r.idCatSeveridad ??
      r.idCatClasSev ??
      r.idApoyoLista ??
      r.idCatNivelRiesgo
    );
  }
}
